/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  DeleteAdminV1StoresStoreIdDiscountsIdCodesCodeIdPathParameters,
  DeleteAdminV1StoresStoreIdDiscountsIdPathParameters,
  Discount,
  DiscountCode,
  GetAdminV1StoresStoreIdDiscounts200,
  GetAdminV1StoresStoreIdDiscountsIdCodes200,
  GetAdminV1StoresStoreIdDiscountsIdCodesParams,
  GetAdminV1StoresStoreIdDiscountsIdCodesPathParameters,
  GetAdminV1StoresStoreIdDiscountsIdPathParameters,
  GetAdminV1StoresStoreIdDiscountsIdUsages200,
  GetAdminV1StoresStoreIdDiscountsIdUsagesParams,
  GetAdminV1StoresStoreIdDiscountsIdUsagesPathParameters,
  GetAdminV1StoresStoreIdDiscountsParams,
  GetAdminV1StoresStoreIdDiscountsPathParameters,
  PatchAdminV1StoresStoreIdDiscountsIdBody,
  PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdBody,
  PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdPathParameters,
  PatchAdminV1StoresStoreIdDiscountsIdPathParameters,
  PostAdminV1StoresStoreIdDiscountsBody,
  PostAdminV1StoresStoreIdDiscountsIdCodesBody,
  PostAdminV1StoresStoreIdDiscountsIdCodesPathParameters,
  PostAdminV1StoresStoreIdDiscountsPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Discounts of the store, newest first
 */
export const getAdminV1StoresStoreIdDiscounts = (
    { storeId }: GetAdminV1StoresStoreIdDiscountsPathParameters,
    params?: GetAdminV1StoresStoreIdDiscountsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdDiscounts200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdDiscounts200>(
      {url: `/admin/v1/stores/${storeId}/discounts`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Code discounts are created with their first codes; automatic ones apply whenever their conditions match. Stacking follows combinesWith (doc 04)
 * @summary Create a discount
 */
export const postAdminV1StoresStoreIdDiscounts = (
    { storeId }: PostAdminV1StoresStoreIdDiscountsPathParameters,
    postAdminV1StoresStoreIdDiscountsBody: BodyType<PostAdminV1StoresStoreIdDiscountsBody>,
 options?: SecondParameter<typeof customInstance<Discount>>,) => {
      return customInstance<Discount>(
      {url: `/admin/v1/stores/${storeId}/discounts`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdDiscountsBody
    },
      options);
    }
  /**
 * `codes` holds the oldest 50; `codeCount` the total. Page through all of them at .../codes
 * @summary One discount with its first codes
 */
export const getAdminV1StoresStoreIdDiscountsId = (
    { storeId, id }: GetAdminV1StoresStoreIdDiscountsIdPathParameters,
 options?: SecondParameter<typeof customInstance<Discount>>,) => {
      return customInstance<Discount>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Orders already placed keep their applied amounts
 * @summary Update a discount
 */
export const patchAdminV1StoresStoreIdDiscountsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdDiscountsIdPathParameters,
    patchAdminV1StoresStoreIdDiscountsIdBody: BodyType<PatchAdminV1StoresStoreIdDiscountsIdBody>,
 options?: SecondParameter<typeof customInstance<Discount>>,) => {
      return customInstance<Discount>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdDiscountsIdBody
    },
      options);
    }
  /**
 * @summary Delete a discount and its codes
 */
export const deleteAdminV1StoresStoreIdDiscountsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdDiscountsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * `q` matches part of a code, case-insensitive; `isActive` filters switched-off codes in or out
 * @summary Codes of the discount, newest first
 */
export const getAdminV1StoresStoreIdDiscountsIdCodes = (
    { storeId, id }: GetAdminV1StoresStoreIdDiscountsIdCodesPathParameters,
    params?: GetAdminV1StoresStoreIdDiscountsIdCodesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdDiscountsIdCodes200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdDiscountsIdCodes200>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}/codes`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Add codes, given or generated
 */
export const postAdminV1StoresStoreIdDiscountsIdCodes = (
    { storeId, id }: PostAdminV1StoresStoreIdDiscountsIdCodesPathParameters,
    postAdminV1StoresStoreIdDiscountsIdCodesBody: BodyType<PostAdminV1StoresStoreIdDiscountsIdCodesBody>,
 options?: SecondParameter<typeof customInstance<Discount>>,) => {
      return customInstance<Discount>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}/codes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdDiscountsIdCodesBody
    },
      options);
    }
  /**
 * A switched-off code is refused at checkout like an unknown one; the discount and its other codes keep working
 * @summary Switch one code off or on
 */
export const patchAdminV1StoresStoreIdDiscountsIdCodesCodeId = (
    { storeId, id, codeId }: PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdPathParameters,
    patchAdminV1StoresStoreIdDiscountsIdCodesCodeIdBody: BodyType<PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdBody>,
 options?: SecondParameter<typeof customInstance<DiscountCode>>,) => {
      return customInstance<DiscountCode>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}/codes/${codeId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdDiscountsIdCodesCodeIdBody
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE once the code was used on an order (switch it off instead) and 409 for the last code of a code discount
 * @summary Delete an unused code
 */
export const deleteAdminV1StoresStoreIdDiscountsIdCodesCodeId = (
    { storeId, id, codeId }: DeleteAdminV1StoresStoreIdDiscountsIdCodesCodeIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}/codes/${codeId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Amounts are in each order's currency
 * @summary Orders that used the discount, newest first
 */
export const getAdminV1StoresStoreIdDiscountsIdUsages = (
    { storeId, id }: GetAdminV1StoresStoreIdDiscountsIdUsagesPathParameters,
    params?: GetAdminV1StoresStoreIdDiscountsIdUsagesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdDiscountsIdUsages200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdDiscountsIdUsages200>(
      {url: `/admin/v1/stores/${storeId}/discounts/${id}/usages`, method: 'GET',
        params
    },
      options);
    }
  export type GetAdminV1StoresStoreIdDiscountsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDiscounts>>>
export type PostAdminV1StoresStoreIdDiscountsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDiscounts>>>
export type GetAdminV1StoresStoreIdDiscountsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDiscountsId>>>
export type PatchAdminV1StoresStoreIdDiscountsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdDiscountsId>>>
export type DeleteAdminV1StoresStoreIdDiscountsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdDiscountsId>>>
export type GetAdminV1StoresStoreIdDiscountsIdCodesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDiscountsIdCodes>>>
export type PostAdminV1StoresStoreIdDiscountsIdCodesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdDiscountsIdCodes>>>
export type PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdDiscountsIdCodesCodeId>>>
export type DeleteAdminV1StoresStoreIdDiscountsIdCodesCodeIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdDiscountsIdCodesCodeId>>>
export type GetAdminV1StoresStoreIdDiscountsIdUsagesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdDiscountsIdUsages>>>
